import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Sparkles,
  Cloud,
  Lock,
  Server,
} from 'lucide-react';
import { Button, FeatureIcon } from '@/components/ui';
import { Card } from '@/components/ui/Card';
import { EraProgression, HowItWorksFlow } from '@/components/charts';

export function LandingPage() {
  return (
    <main className="flex-1 flex flex-col overflow-y-auto">
      {/* Hero */}
      <section className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 pt-20 pb-12 md:py-24 relative">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 rounded-full mb-8 border border-indigo-500/20 shadow-sm shadow-indigo-500/10">
          <Sparkles className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-medium text-indigo-300">The Post-UI Platform</span>
        </div>

        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-hero-mobile md:text-hero-desktop font-extrabold tracking-heading mb-8">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-slate-400">
              Stop Clicking.
            </span>
            <br />
            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Start Talking.
            </span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Turn your business into a conversational service.
            Customers chat. AI agents connect via MCP.{' '}
            <span className="text-slate-200">Your data stays where you put it.</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo">
              <Button
                variant="primary"
                size="lg"
                className="w-full sm:w-auto"
                rightIcon={<ArrowRight className="w-4 h-4" />}
              >
                Try Live Demo
              </Button>
            </Link>
            <Link to="/features">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                See Features
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* The Shift */}
      <section className="px-4 sm:px-6 py-12 md:py-20 bg-slate-800/20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3 text-center">
            Yesterday. Today. Tomorrow.
          </h2>
          <p className="text-sm sm:text-base text-slate-400 text-center max-w-2xl mx-auto mb-10 md:mb-14 leading-relaxed">
            How people reach a business is changing twice over — first from clicking to talking, then
            from talking themselves to sending an agent.
          </p>
          <EraProgression />
        </div>
      </section>

      {/* How It Works */}
      <section className="px-4 sm:px-6 py-12 md:py-20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-10 md:mb-14 text-center">
            Three Steps. That&apos;s It.
          </h2>
          <HowItWorksFlow />
        </div>
      </section>

      {/* Where it runs */}
      <section className="px-4 sm:px-6 py-12 md:py-20 bg-slate-800/20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3 text-center">
            Where It Runs Is Your Choice
          </h2>
          <p className="text-sm sm:text-base text-slate-400 text-center max-w-2xl mx-auto mb-10 md:mb-14 leading-relaxed">
            DialogMe runs on its own AI stack, Fnk, rather than a model rented per token — so you
            are not tied to a model vendor, and the same stack can run inside your own network.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <Card variant="default" className="p-6 text-center">
              <FeatureIcon gradient="from-emerald-500/20 to-teal-500/20" className="mx-auto mb-4">
                <Cloud className="w-6 h-6 text-emerald-400" />
              </FeatureIcon>
              <h3 className="text-base font-semibold text-slate-100 tracking-tight mb-1">Managed</h3>
              <p className="text-sm text-slate-400 leading-relaxed">We run it. Live in minutes.</p>
            </Card>
            <Card variant="default" className="p-6 text-center">
              <FeatureIcon gradient="from-cyan-500/20 to-blue-500/20" className="mx-auto mb-4">
                <Lock className="w-6 h-6 text-cyan-400" />
              </FeatureIcon>
              <h3 className="text-base font-semibold text-slate-100 tracking-tight mb-1">Your Private Cloud</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Your tenancy. Nothing leaves it.</p>
            </Card>
            <Card variant="default" className="p-6 text-center">
              <FeatureIcon gradient="from-indigo-500/20 to-purple-500/20" className="mx-auto mb-4">
                <Server className="w-6 h-6 text-indigo-400" />
              </FeatureIcon>
              <h3 className="text-base font-semibold text-slate-100 tracking-tight mb-1">On-Premises</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Our own stack, delivered as Fnk.</p>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Link to="/enterprise">
              <Button variant="outline" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Explore Enterprise
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 py-12 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <Card variant="cta" className="p-6 sm:p-8 md:p-10">
            <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3">
              Ready for the Post-UI World?
            </h2>
            <p className="text-slate-300 mb-8 leading-relaxed">
              No credit card. No setup. Just conversation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing">
                <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="w-4 h-4" />}>
                  View Pricing
                </Button>
              </Link>
              <Link to="/demo">
                <Button variant="ghost" size="lg" className="w-full sm:w-auto">
                  Try Live Demo
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </main>
  );
}
